import { Link } from '@tanstack/react-router'
import {
  Fragment,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'
import {
  ArrowDownWideNarrow,
  ArrowUpRight,
  ChevronDown,
  ChevronsUpDown,
  ChevronRight,
  Columns3,
  Search,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import {
  formatPercentage,
  formatUsdMillions,
} from '@/features/actual-vs-budget/format'
import {
  buildPortfolioRows,
  isPortfolioSort,
  portfolioSortOptions,
  selectPortfolioRows,
  type PortfolioMetricSummary,
  type PortfolioStatus,
} from '@/features/actual-vs-budget/portfolio'
import type { PortfolioMatrixState } from '@/features/actual-vs-budget/portfolio-matrix-state'
import type {
  ActualVsBudgetCompanyFixture,
  ActualVsBudgetMetricKey,
} from '@/features/actual-vs-budget/types'

const metricOptions: readonly {
  key: ActualVsBudgetMetricKey
  label: string
}[] = [
  { key: 'revenue', label: 'Revenue' },
  { key: 'adjusted-ebitda', label: 'Adjusted EBITDA' },
]

const statusContent: Record<PortfolioStatus, { label: string }> = {
  favorable: { label: 'On plan' },
  watch: { label: 'Watch' },
  unfavorable: { label: 'Needs attention' },
  neutral: { label: 'Informational' },
  unavailable: { label: 'Unavailable' },
}

const statusTextColor: Record<PortfolioStatus, string> = {
  favorable: 'text-green-700',
  watch: 'text-amber-700',
  unfavorable: 'text-destructive',
  neutral: 'text-primary',
  unavailable: 'text-muted-foreground',
}

const legendSwatchColor: Record<PortfolioStatus, string> = {
  favorable: 'bg-green-700',
  watch: 'bg-amber-700',
  unfavorable: 'bg-destructive',
  neutral: 'bg-primary',
  unavailable: 'bg-muted-foreground',
}

const exceptionStatuses: readonly PortfolioStatus[] = ['watch', 'unfavorable']

function LegendItem({ status }: { status: PortfolioStatus }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden="true"
        className={`size-2 shrink-0 rounded-full ${legendSwatchColor[status]}`}
      />
      <Typography as="span" variant="muted" className="text-xs">
        {statusContent[status].label}
      </Typography>
    </span>
  )
}

function ComparisonHeader({
  basis,
  dropDivider,
  width,
}: {
  basis: string
  dropDivider: boolean
  width: 'min-w-45' | 'min-w-48'
}) {
  return (
    <TableHead
      className={`h-10 ${width} bg-primary/[0.04] px-4 py-2 align-bottom${
        dropDivider ? ' border-r-0' : ''
      }`}
    >
      <Typography
        as="span"
        variant="body-sm"
        className="block text-[11px] font-normal text-muted-foreground"
      >
        {basis}
      </Typography>
    </TableHead>
  )
}

function ComparisonCell({
  status,
  unavailableReason,
  value,
}: {
  status: PortfolioStatus
  unavailableReason: string
  value: number | null
}) {
  const statusLabel = statusContent[status].label
  const isException = exceptionStatuses.includes(status)

  if (value === null) {
    return (
      <div className="flex flex-col items-end text-right">
        <Tooltip>
          <TooltipTrigger asChild>
            <Typography
              as="span"
              variant="body-sm"
              tabIndex={0}
              aria-label={unavailableReason}
              className="block cursor-default rounded-sm text-[13px] font-medium tabular-nums text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              —
            </Typography>
          </TooltipTrigger>
          <TooltipContent>{unavailableReason}</TooltipContent>
        </Tooltip>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-end gap-0.5 text-right">
      <Typography
        as="span"
        variant="body-sm"
        className={`block text-[13px] font-medium tabular-nums ${statusTextColor[status]}`}
      >
        {formatPercentage(value)}
        <span className="sr-only"> — {statusLabel}</span>
      </Typography>
      {isException && (
        <Typography
          as="span"
          variant="body-sm"
          aria-hidden="true"
          className={`block text-[10px] font-medium uppercase tracking-[0.06em] ${statusTextColor[status]}`}
        >
          {statusLabel}
        </Typography>
      )}
    </div>
  )
}

interface EvidenceGroupProps {
  budget: number | null
  primaryLabel: 'Actual' | 'Forecast'
  primaryValue: number | null
  title: string
  varianceAmount: number | null
  variancePercent: number | null
}

function EvidenceValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <Typography
        as="span"
        variant="body-sm"
        className="block text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground"
      >
        {label}
      </Typography>
      <Typography
        as="span"
        variant="body-sm"
        className="mt-1 block truncate text-xs font-normal tabular-nums text-primary"
      >
        {value}
      </Typography>
    </div>
  )
}

function EvidenceGroup({
  budget,
  primaryLabel,
  primaryValue,
  title,
  varianceAmount,
  variancePercent,
}: EvidenceGroupProps) {
  const missingLabel =
    primaryValue === null
      ? primaryLabel === 'Forecast'
        ? 'Forecast unavailable'
        : 'Actuals unavailable'
      : budget === null
        ? 'Budget unavailable'
        : null

  return (
    <div className="rounded-md border border-primary/10 bg-background p-3">
      <Typography
        as="h4"
        variant="body-sm"
        className="font-medium text-primary"
      >
        {title}
      </Typography>
      {missingLabel && (
        <Typography variant="muted" className="mt-1 text-xs">
          {missingLabel}
        </Typography>
      )}
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <EvidenceValue
          label={primaryLabel}
          value={
            primaryValue === null
              ? `${primaryLabel} unavailable`
              : formatUsdMillions(primaryValue, '—')
          }
        />
        <EvidenceValue
          label="Budget"
          value={
            budget === null ? 'Budget unavailable' : formatUsdMillions(budget)
          }
        />
        <EvidenceValue
          label="Amount variance"
          value={formatUsdMillions(varianceAmount, '—')}
        />
        <EvidenceValue
          label="Percent variance"
          value={formatPercentage(variancePercent, '—')}
        />
      </div>
    </div>
  )
}

function EvidenceValues({ metric }: { metric: PortfolioMetricSummary }) {
  return (
    <div className="grid gap-3 xl:grid-cols-2">
      <EvidenceGroup
        title="FYTD Actual vs Budget"
        primaryLabel="Actual"
        primaryValue={metric.fytd.actual}
        budget={metric.fytd.budget}
        varianceAmount={metric.fytd.varianceAmount}
        variancePercent={metric.fytd.variancePercent}
      />
      <EvidenceGroup
        title="Full-year Forecast vs Budget"
        primaryLabel="Forecast"
        primaryValue={metric.fullYearOutlook.forecast}
        budget={metric.fullYearOutlook.budget}
        varianceAmount={metric.fullYearOutlook.varianceAmount}
        variancePercent={metric.fullYearOutlook.variancePercent}
      />
    </div>
  )
}

interface PortfolioMatrixProps {
  companies: readonly ActualVsBudgetCompanyFixture[]
  state: PortfolioMatrixState
  onStateChange: Dispatch<SetStateAction<PortfolioMatrixState>>
}

export function PortfolioMatrix({
  companies,
  state,
  onStateChange,
}: PortfolioMatrixProps) {
  const allRows = useMemo(() => buildPortfolioRows(companies), [companies])
  const funds = useMemo(
    () => [...new Set(allRows.map(({ fund }) => fund))].sort(),
    [allRows]
  )
  const { fund, query, sort, visibleMetrics } = state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(() => new Set())

  const rows = useMemo(
    () =>
      selectPortfolioRows(allRows, {
        query,
        fund,
        sort,
        visibleMetrics,
      }),
    [allRows, fund, query, sort, visibleMetrics]
  )
  const metricNoun = visibleMetrics.length === 1 ? 'metric' : 'metrics'
  const columnCount = 1 + visibleMetrics.length * 2

  function toggleMetric(key: ActualVsBudgetMetricKey, checked: boolean) {
    onStateChange((current) => {
      const nextVisibleMetrics = checked
        ? current.visibleMetrics.includes(key)
          ? current.visibleMetrics
          : [...current.visibleMetrics, key]
        : current.visibleMetrics.length === 1
          ? current.visibleMetrics
          : current.visibleMetrics.filter((item) => item !== key)

      return { ...current, visibleMetrics: nextVisibleMetrics }
    })
  }

  function toggleRow(rowId: string) {
    setExpandedRows((current) => {
      const next = new Set(current)
      if (next.has(rowId)) next.delete(rowId)
      else next.add(rowId)
      return next
    })
  }

  function updateSort(value: string) {
    if (isPortfolioSort(value)) {
      onStateChange((current) => ({ ...current, sort: value }))
    }
  }

  return (
    <TooltipProvider delayDuration={150}>
      <section aria-labelledby="portfolio-matrix-heading" className="space-y-4">
        <div className="flex flex-col justify-between gap-3 2xl:flex-row 2xl:items-end">
          <div>
            <Typography
              variant="body-sm"
              className="font-semibold uppercase tracking-[0.16em] text-muted-foreground"
            >
              Portfolio attention index
            </Typography>
            <Typography
              as="h2"
              variant="h5"
              id="portfolio-matrix-heading"
              className="mt-1 text-lg font-semibold text-primary"
            >
              Latest closed FYTD · full-year forecast
            </Typography>
            <Typography variant="muted" className="mt-1 max-w-3xl">
              Start with the largest unfavorable FYTD or full-year Forecast
              variance. Expand a company for supporting values, then open its
              review for detail.
            </Typography>
          </div>
          <div
            className="flex flex-wrap items-center gap-x-3 gap-y-1.5"
            aria-label="Status legend"
          >
            <Typography variant="muted" className="mr-1 text-xs">
              Illustrative thresholds
            </Typography>
            <LegendItem status="favorable" />
            <LegendItem status="watch" />
            <LegendItem status="unfavorable" />
            <LegendItem status="unavailable" />
          </div>
        </div>

        <div className="grid gap-2 rounded-lg border border-primary/10 bg-muted/25 p-3 md:grid-cols-2 xl:grid-cols-[minmax(240px,1fr)_190px_210px_200px]">
          <div className="relative">
            <Search
              aria-hidden="true"
              className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              value={query}
              onChange={(event) =>
                onStateChange((current) => ({
                  ...current,
                  query: event.target.value,
                }))
              }
              placeholder="Search companies"
              aria-label="Search companies"
              className="bg-background pl-9"
            />
          </div>

          <Select
            value={fund}
            onValueChange={(value) =>
              onStateChange((current) => ({ ...current, fund: value }))
            }
          >
            <SelectTrigger
              aria-label="Filter by fund"
              className="bg-background"
            >
              <SelectValue placeholder="All funds" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All funds</SelectItem>
              {funds.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="justify-between bg-background"
              >
                <div className="flex items-center gap-2">
                  <Columns3 aria-hidden="true" className="size-4" />
                  <Typography
                    as="span"
                    variant="body-sm"
                    className="font-medium"
                  >
                    Display · {visibleMetrics.length} {metricNoun}
                  </Typography>
                </div>
                <ChevronsUpDown
                  aria-hidden="true"
                  className="size-4 opacity-60"
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel>Visible metrics</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {metricOptions.map((option) => {
                const checked = visibleMetrics.includes(option.key)
                return (
                  <DropdownMenuCheckboxItem
                    key={option.key}
                    checked={checked}
                    disabled={checked && visibleMetrics.length === 1}
                    onCheckedChange={(nextChecked) =>
                      toggleMetric(option.key, nextChecked)
                    }
                    onSelect={(event) => event.preventDefault()}
                  >
                    {option.label}
                  </DropdownMenuCheckboxItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          <Select value={sort} onValueChange={updateSort}>
            <SelectTrigger
              aria-label="Sort companies"
              className="bg-background"
            >
              <div className="flex items-center gap-2">
                <ArrowDownWideNarrow aria-hidden="true" className="size-4" />
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent>
              {portfolioSortOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-hidden rounded-sm border border-primary/20 bg-white">
          <div className="max-w-full overflow-x-auto">
            <Table
              disableWrapper
              className="min-w-245 table-auto [&_th]:border-primary/20 [&_td]:border-primary/20 [&_th:first-child]:border-l-0 [&_td:first-child]:border-l-0 [&_th:last-child]:border-r-0 [&_td:last-child]:border-r-0 [&_tbody_tr:last-child_td]:border-b-0"
            >
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead
                    rowSpan={2}
                    className="sticky left-0 z-20 w-70 bg-primary/[0.08] px-4 py-3 align-bottom"
                  >
                    <Typography as="span" variant="body-sm">
                      Company
                    </Typography>
                  </TableHead>
                  {visibleMetrics.map((key) => (
                    <TableHead
                      key={`${key}-group`}
                      colSpan={2}
                      className="bg-primary/[0.08] px-4 py-2 text-center"
                    >
                      <Typography
                        as="span"
                        variant="body-sm"
                        className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-primary"
                      >
                        {allRows[0]?.metrics[key].label}
                      </Typography>
                    </TableHead>
                  ))}
                </TableRow>
                <TableRow className="hover:bg-transparent">
                  {visibleMetrics.flatMap((key) => [
                    <ComparisonHeader
                      key={`${key}-fytd`}
                      basis="FYTD Actual vs Budget"
                      width="min-w-45"
                      dropDivider
                    />,
                    <ComparisonHeader
                      key={`${key}-forecast`}
                      basis="Full-year Forecast vs Budget"
                      width="min-w-48"
                      dropDivider={false}
                    />,
                  ])}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const isExpanded = expandedRows.has(row.id)

                  return (
                    <Fragment key={row.id}>
                      <TableRow className="group align-middle">
                        <TableCell className="sticky left-0 z-10 bg-background px-4 py-4 group-hover:bg-muted/50">
                          <Button
                            type="button"
                            variant="ghost"
                            className="h-auto w-full justify-start gap-2 p-0 text-left hover:bg-transparent"
                            aria-expanded={isExpanded}
                            aria-controls={`${row.id}-evidence`}
                            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${row.name} comparison details`}
                            onClick={() => toggleRow(row.id)}
                          >
                            {isExpanded ? (
                              <ChevronDown
                                aria-hidden="true"
                                className="size-4 shrink-0 text-primary transition-colors"
                              />
                            ) : (
                              <ChevronRight
                                aria-hidden="true"
                                className="size-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary"
                              />
                            )}
                            <span className="min-w-0">
                              <Typography
                                as="span"
                                variant="body-sm"
                                className="block truncate text-[13px] font-medium text-primary"
                              >
                                {row.name}
                              </Typography>
                              <Typography
                                as="span"
                                variant="body-sm"
                                className="mt-0.5 block whitespace-normal text-[11px] leading-snug text-muted-foreground"
                              >
                                {row.fund} · {row.fiscalYearLabel} ·{' '}
                                {row.periodLabel}
                              </Typography>
                            </span>
                          </Button>
                        </TableCell>
                        {visibleMetrics.flatMap((key) => {
                          const metric = row.metrics[key]
                          const fytdReason =
                            metric.fytd.actual === null
                              ? 'Actuals unavailable'
                              : 'Budget unavailable'
                          const forecastReason =
                            metric.fullYearOutlook.forecast === null
                              ? 'Forecast unavailable'
                              : 'Budget unavailable'

                          return [
                            <TableCell
                              key={`${key}-fytd`}
                              className="border-r-0 px-4 py-4"
                            >
                              <ComparisonCell
                                value={metric.fytd.variancePercent}
                                status={metric.fytd.status}
                                unavailableReason={fytdReason}
                              />
                            </TableCell>,
                            <TableCell
                              key={`${key}-forecast`}
                              className="px-4 py-4"
                            >
                              <ComparisonCell
                                value={metric.fullYearOutlook.variancePercent}
                                status={metric.fullYearOutlook.status}
                                unavailableReason={forecastReason}
                              />
                            </TableCell>,
                          ]
                        })}
                      </TableRow>
                      {isExpanded && (
                        <TableRow
                          id={`${row.id}-evidence`}
                          className="hover:bg-transparent"
                        >
                          <TableCell
                            colSpan={columnCount}
                            className="bg-muted/20 px-4 py-4 whitespace-normal"
                          >
                            <div className="space-y-4">
                              {visibleMetrics.map((key) => (
                                <div
                                  key={key}
                                  className="grid gap-3 lg:grid-cols-[150px_1fr]"
                                >
                                  <Typography
                                    as="h3"
                                    variant="body-sm"
                                    className="pt-1 font-medium text-primary"
                                  >
                                    {row.metrics[key].label}
                                  </Typography>
                                  <EvidenceValues metric={row.metrics[key]} />
                                </div>
                              ))}
                              <div className="flex justify-end border-t border-primary/8 pt-3">
                                <Link
                                  to="/experiments/actual-vs-budget"
                                  search={{ company: row.id }}
                                  className="inline-flex items-center gap-1.5 rounded-sm text-sm font-medium text-primary underline-offset-4 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                                >
                                  <Typography
                                    as="span"
                                    variant="body-sm"
                                    className="font-medium"
                                  >
                                    Open company review
                                  </Typography>
                                  <ArrowUpRight
                                    aria-hidden="true"
                                    className="size-4"
                                  />
                                </Link>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  )
                })}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={columnCount}
                      className="h-36 text-center whitespace-normal"
                    >
                      <Typography className="font-medium text-primary">
                        {companies.length === 0
                          ? 'No portfolio companies are available.'
                          : 'No companies match these controls.'}
                      </Typography>
                      <Typography variant="muted" className="mt-1">
                        {companies.length === 0
                          ? 'Portfolio data will appear here when it is available.'
                          : 'Clear the search or choose a different fund.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-col gap-1 border-t border-primary/20 bg-muted/20 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between">
            <Typography variant="muted" className="text-xs">
              Showing {rows.length} of {allRows.length} companies
            </Typography>
            <Typography variant="muted" className="text-xs sm:text-right">
              On plan ≤2% unfavorable · Watch &gt;2% and &lt;10% · Needs
              attention ≥10%
            </Typography>
          </div>
        </div>
      </section>
    </TooltipProvider>
  )
}
